import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActionList, debounce, IIconTextLinkProps } from "component-library";

import {
  IAgencyList,
  ICastingNotes,
  INewShortlist,
  IShortlistPerformer,
  IShortlists,
} from "models";

import { ChatBoxIcon, NotesIcon, PrintIcon } from "assets";
import { CastingDirectorNotes } from "pages/Profile/components/ActionFooterSection/ActionsFooterRow/CastingDirectorNotes";
import { ShortlistSection } from "pages/Profile/components/ActionFooterSection/ActionsFooterRow/ShortlistSection";

import "./ActionsFooterRow.scss";

export interface IActionsFooterRowContent {
  shortlistLabel: string;
  shortlistPlaceholder: string;
  shortlistSearchPlaceholder: string;
  newShortlistLabel: string;
  newShortlistOption: string;
  shortlistRequired: string;
  shortlistNameRequired: string;
  addToShortlistLabel: string;
  printLabel: string;
  emailLabel: string;
  viewNotesLabel: string;
  addToShortlistHeader: string;
  saveShortlist: string;
  cancelShortlist: string;
  moreButtonLabel: string;
  performerAddedToast: string;
}

export interface IActionsFooterRowProps {
  content: IActionsFooterRowContent;
  shortlists?: IShortlists;
  agencies?: IAgencyList;
  notes?: ICastingNotes;
  saveNotes: (notes: ICastingNotes) => void;
  printPage: () => void;
  sendEmail: () => void;
  initShortlistLoading?: () => void;
  addShortList: (model: INewShortlist) => Promise<string>;
  addPerformerShortList: (
    shortlistId: number,
    model: IShortlistPerformer
  ) => Promise<string>;
}

const ActionsFooterRow = ({
  content,
  shortlists,
  agencies,
  notes: notesModel,
  saveNotes: saveNotesProp,
  printPage,
  sendEmail,
  initShortlistLoading,
  addShortList,
  addPerformerShortList,
}: IActionsFooterRowProps) => {
  const [notes, setNotes] = useState<string | undefined>(
    () => notesModel?.content
  );
  const [isNotesVisible, setIsNotesVisible] = useState(
    () => (notesModel?.content?.length ?? 0) > 0
  );

  useEffect(() => {
    if (notesModel && notes === undefined) {
      setNotes(notesModel.content);
      setIsNotesVisible(notesModel.content.length > 0);
    }
  }, [notesModel, notes]);

  useEffect(() => {
    initShortlistLoading?.();
  }, [initShortlistLoading]);

  const saveNotes = useCallback(() => {
    const notesText = notes ?? "";
    const prevContent = notesModel?.content ?? "";

    if (prevContent !== notesText) {
      saveNotesProp({ content: notesText });
    }
  }, [notes, notesModel, saveNotesProp]);

  const saveNotesRef = useRef(saveNotes);
  saveNotesRef.current = saveNotes;
  const saveNotesDebounced = useMemo(
    () => debounce(() => saveNotesRef.current(), 1000),
    []
  );

  const openNotes = useCallback(() => setIsNotesVisible(true), []);
  const closeNotes = useCallback(() => {
    setIsNotesVisible(false);
    saveNotes();
  }, [saveNotes]);

  const changeNotes = useCallback(
    (newNotes: string) => {
      setNotes(newNotes);
      saveNotesDebounced();
    },
    [saveNotesDebounced]
  );

  const toggleNotes = useCallback(
    () => (isNotesVisible ? closeNotes() : openNotes()),
    [isNotesVisible, closeNotes, openNotes]
  );

  const notesActions: IIconTextLinkProps[] = useMemo(
    () => [
      {
        text: content.viewNotesLabel,
        svgIconStart: <NotesIcon />,
        variant: "secondary",
        handleClick: toggleNotes,
        id: "notesButton",
      },
      {
        text: content.printLabel,
        svgIconStart: <PrintIcon />,
        variant: "secondary",
        handleClick: printPage,
        id: "printButton",
      },
      {
        text: content.emailLabel,
        svgIconStart: <ChatBoxIcon />,
        variant: "secondary",
        handleClick: sendEmail,
        id: "emailButton",
      },
    ],
    [
      content.viewNotesLabel,
      content.printLabel,
      content.emailLabel,
      toggleNotes,
      printPage,
      sendEmail,
    ]
  );

  const notesContainerClasses = `c-actions-footer-row__notes-container ${
    isNotesVisible ? "active" : ""
  }`;
  const notesClasses = `c-actions-footer-row__notes ${
    isNotesVisible ? "active" : ""
  }`;

  return (
    <div className="c-actions-footer-row g-col-lg-12 g-col-md-12 g-inner-grid">
      <div className="g-col-lg-4 g-col-md-5 g-col-sm-12">
        <div className="c-actions-footer-row__notes-anchor">
          <div className={notesContainerClasses}>
            <div className={notesClasses}>
              <CastingDirectorNotes
                notes={notes}
                changeNotes={changeNotes}
                closeNotes={closeNotes}
              />
            </div>
          </div>
        </div>
        <div className="c-actions-footer-row__button">
          <ActionList
            variant="pageHeader"
            items={notesActions}
            moreButtonText={content.moreButtonLabel}
            moreElementsDirection="up"
          />
        </div>
      </div>
      <div className="c-actions-footer-row__notes-buffer g-col-lg-1" />
      <ShortlistSection
        addPerformerShortList={addPerformerShortList}
        addShortList={addShortList}
        content={content}
        agencies={agencies}
        shortlists={shortlists}
      />
    </div>
  );
};

export default ActionsFooterRow;
